// apps/client/src/components/loans/LoanSearchSelect.tsx
// Controlled combobox for picking a single loan by client name or Valutten
// loan ID. Server-filtered via useLoanSearch (searchLoans callable). Rendered
// in NORMAL DOCUMENT FLOW (not an absolute popover): it lives inside table
// rows wrapped in overflow-x-auto / overflow-hidden, which would clip an
// absolute dropdown; an in-flow list grows the row safely and sidesteps the
// blur-vs-click race a floating popover introduces.

import React, { useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { Search, Loader2, Banknote, X } from 'lucide-react';
import { useLoanSearch, type LoanSearchResult } from '../../hooks/useLoanSearch';

export type { LoanSearchResult };

interface LoanSearchSelectProps {
  value: LoanSearchResult | null;
  onChange: (loan: LoanSearchResult | null) => void;
  purpose: 'all' | 'refinance_successor';
  label: string;
  disabled?: boolean;
  variant?: 'default' | 'manual';
  autoFocus?: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

// lender · $amount · date · #loanId. The amount is omitted entirely when it is
// 0 or missing (hidden-commission users), so "$0" never renders. An empty
// settlement date is dropped too, so no dangling separator shows.
function summaryLine(loan: LoanSearchResult): string {
  const parts: string[] = [loan.lender];
  if (loan.loanAmountCents && loan.loanAmountCents > 0) {
    parts.push(formatCurrency(loan.loanAmountCents));
  }
  if (loan.settlementDate) {
    parts.push(loan.settlementDate);
  }
  parts.push(`#${loan.loanId}`);
  return parts.filter(Boolean).join(' · ');
}

const RESULT_ROW_CLASS =
  'flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-300 hover:bg-gray-800 hover:text-white data-[selected=true]:bg-blue-600/20 data-[selected=true]:text-white outline-none';

const RESULTS_CONTAINER_CLASS =
  'mt-2 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-h-[400px] overflow-y-auto p-2';

const LoanSearchSelect: React.FC<LoanSearchSelectProps> = ({
  value,
  onChange,
  purpose,
  label,
  disabled = false,
  variant = 'default',
  autoFocus = false,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  // Escape / click-outside collapse the in-flow list without discarding the
  // typed query. Typing (or reopening) clears it.
  const [dismissed, setDismissed] = useState(false);

  const { loans, isLoading, error } = useLoanSearch(query, {
    enabled: !disabled && value === null,
    purpose,
  });

  const showResults =
    value === null && !disabled && query.trim().length >= 2 && !dismissed;

  // Click-outside collapses the list (in-flow, so this is the only "close"
  // affordance beyond Escape and selecting a row).
  useEffect(() => {
    if (!showResults) return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setDismissed(true);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showResults]);

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setDismissed(false);
  };

  const handleSelect = (loan: LoanSearchResult) => {
    onChange(loan);
    setDismissed(true);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setDismissed(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setDismissed(true);
    }
  };

  const inputSkin =
    variant === 'manual'
      ? 'bg-gray-900 border border-gray-700'
      : 'bg-gray-700 border border-gray-600';
  const inputClass = `block w-full pl-10 pr-3 py-2.5 ${inputSkin} rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm`;
  const chipClass = `flex items-start justify-between gap-3 px-3 py-2.5 ${inputSkin} rounded-lg`;

  return (
    <div ref={rootRef}>
      {/* Visible label. The programmatic input association is handled by cmdk's
          `label` prop below (it wires a hidden label's for/id to the input's
          aria-labelledby), since cmdk owns the input's generated id. */}
      <span className="block text-xs text-gray-400 mb-1">{label}</span>

      {value !== null ? (
        <div className={chipClass}>
          <div className="min-w-0">
            <div className="font-semibold text-white truncate">{value.borrowerName}</div>
            <div className="text-xs text-gray-400 truncate">{summaryLine(value)}</div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear selected loan"
            className="text-gray-400 hover:text-white flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <Command shouldFilter={false} label={label} onKeyDown={handleKeyDown}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Command.Input
              value={query}
              onValueChange={handleQueryChange}
              placeholder="Search loans"
              disabled={disabled}
              autoFocus={autoFocus}
              className={inputClass}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Search by client name or Valutten loan ID
          </p>

          {showResults && (
            <Command.List className={RESULTS_CONTAINER_CLASS}>
              {isLoading ? (
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching...
                </div>
              ) : error ? (
                <div className="px-3 py-2 text-sm text-red-300">{error}</div>
              ) : loans.length > 0 ? (
                loans.map((loan) => (
                  <Command.Item
                    key={loan.loanId}
                    value={loan.loanId}
                    onSelect={() => handleSelect(loan)}
                    className={RESULT_ROW_CLASS}
                  >
                    <Banknote className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-semibold text-white">
                        {loan.borrowerName}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {summaryLine(loan)}
                      </div>
                    </div>
                  </Command.Item>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">No matching loans.</div>
              )}
            </Command.List>
          )}
        </Command>
      )}
    </div>
  );
};

export default LoanSearchSelect;
